import { useState } from 'react';
import PropTypes from 'prop-types';
import {
  A11y,
  Controller,
  EffectFade,
  Mousewheel,
  Navigation,
  Pagination,
} from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import cls from 'classnames';

import { getContent, isDDEnv, isWHEnv } from '@/utils/helper';
import { analytics, getClickCtaDetail } from '@/utils/analytics';
import { useIsKorean } from '@/utils/useLocale';

import Icon from '@/atoms/Icon/Icon';
import Text from '@/atoms/Text/Text';
import Pill from '@/atoms/Pill/Pill';
import CtfCta, { Cta, CtaWrapper } from '@/molecules/CTA/index';
import { DynamicAsset } from '@/molecules/Media/index';
import RichText from '@/molecules/RichText/RichText';

import { CtfCtaStyle } from '@/utils/constants';
import { TEXT_BASE_CLASS } from '@/atoms/Text/Text.constants';

const SWIPER_BREAKPOINTS = {
  360: {
    autoHeight: true,
  },
  640: {
    autoHeight: false,
  },
};

const SWIPER_MODULES = [A11y, Controller, Mousewheel, Navigation, Pagination];

const SWIPER_NAVIGATION = {
  nextEl: '.aaco-carousel-normal__next',
  prevEl: '.aaco-carousel-normal__prev',
};

const SWIPER_PAGINATION = {
  el: '.aaco-carousel-normal__pagination',
  type: 'custom',
  renderCustom: function (_swiper, current, total) {
    const formattedCurrent = current < 10 ? `0${current}` : current;
    const formattedTotal = total < 10 ? `0${total}` : total;
    return `${formattedCurrent}/${formattedTotal}`;
  },
};

const SWIPER_SPEED = 1000;

// @TODO: Update left and right arrow carousel nav with proper icon < >
function CarouselNormal({
  assets = [],
  carouselContentStyle = {
    compactSpace: false,
    hasBackground: false,
    linkStyle: '',
  },
  hasBackground,
  entries = [],
  fields = null,
}) {
  const isKorean = useIsKorean();

  const [firstSwiper, setFirstSwiper] = useState(null);
  const [secondSwiper, setSecondSwiper] = useState(null);
  const [thirdSwiper, setThirdSwiper] = useState(null);

  const {
    callToAction,
    cards,
    contentTags = [],
    featuredCardTitle = false,
    title = '',
    removeYPadding,
    id,
    subTitle,
    // subTitlePadding = false,
    locationText,
    superText,
    backgroundColor,
    hideTitle,
    titleSize,
  } = fields;

  const tag =
    contentTags.length > 0 ? getContent(contentTags[0].sys?.id, entries) : null;
  const isWH = isWHEnv();
  const isDD = isDDEnv();

  return (
    <div
      id={id}
      className={`aaco-carousel-normal overflow-hidden ${isDD ? 'pr-8' : 'py-9'} ${
        !carouselContentStyle?.compactSpace && isDD &&
        `md:pr-0 py-9 ${removeYPadding ? '' : 'md:py-20'}`
      } bg-${backgroundColor}`}
      aria-label={title}
    >
      <div
        className={`${
          !carouselContentStyle?.compactSpace && `container mx-auto ${isWH ? ' px-6 py-9 md:p-10 xl:px-16' : 'md:py-20'}`
        }`}
      >
        <div className="flex flex-wrap justify-between">
          <div
            className={`content w-full mx-auto p-0 z-20 relative m-0 mb-16 ${
              !carouselContentStyle?.compactSpace &&
              'md:w-1/2 lg:w-5/12 md:pl-16 md:pr-6 dd-kr:lg:py-20 dd-en:lg:py-20 md:-my-2 lg:-ml-16 lg:-mr-16 md:-ml-10 lg:-my-11'
            }
            `}
          >
            {featuredCardTitle ? (
              <Swiper
                breakpoints={SWIPER_BREAKPOINTS}
                direction="horizontal"
                effect="fade"
                enabled={false}
                fadeEffect={{ crossFade: true }}
                modules={[...SWIPER_MODULES, EffectFade]}
                mousewheel={{
                  forceToAxis: true,
                }}
                onSwiper={setThirdSwiper}
                speed={SWIPER_SPEED}
              >
                {cards.map((card, index) => {
                  const content = getContent(card.sys?.id, entries);
                  const { primaryCTA, tags = [], text, title } = content.fields;
                  const tag =
                    tags.length > 0
                      ? getContent(tags[0].sys?.id, entries)
                      : null;
                  return (
                    <SwiperSlide key={index}>
                      <div className="content relative">
                        {tag && (
                          <Pill
                            style="pill-action mb-4 inline-flex items-center"
                            onClick={() =>
                              {/* Search disabled */}
                            }
                            small
                          >
                            {tag.fields?.name}
                          </Pill>
                        )}
                        <h2 className="text-h4 mb-5 font-heading">{title}</h2>
                        {text && (
                          <p className="text-grey-6 text-body">{text}</p>
                        )}
                        {primaryCTA && (
                          <CtfCta field={primaryCTA} entries={entries} />
                        )}
                      </div>
                    </SwiperSlide>
                  );
                })}
              </Swiper>
            ) : (
              <div className={`flex flex-col ${locationText ? "justify-between" : "justify-start"} h-full ${isWH ? "max-h-[38rem]" : ""}`}>
                <div className="block">
                  {tag && (
                    <Pill
                      style="pill-action mb-4 inline-flex items-center cursor-default"
                      onClick={() =>
                        {/* Search disabled */}
                      }
                      small
                    >
                      {tag.fields?.name}
                    </Pill>
                  )}
                  {superText && (
                    <Text
                      tag="tag"
                      style="text-black font-pro-mono"
                    >
                      {superText}
                    </Text>
                  )}
                  <Text tag={subTitle && !titleSize ? "h3" : titleSize ? titleSize : "h4"} style={`text-black mb-4 ${hideTitle ? "opacity-0" : ""}`}>{title}</Text>
                  {subTitle && (
                    <hr className={`border-black my-4 md:my-8 ${hideTitle ? "opacity-0" : ""}`} />
                  )}
                </div>
                <div className={`flex flex-col gap-4 justify-end`}>
                  {locationText && (
                    <div className="text-location-carousel-normal">
                      <Text
                        tag="tag"
                        style="text-transparent border-l-1 border-l-transparent pl-4 font-pro-mono"
                      >
                        {locationText}
                      </Text>
                    </div>
                  )}
                  <div className="text-content-carousel-normal grid gap-y-4">
                    {subTitle &&
                      subTitle.content && subTitle.content.map(({ content, nodeType }, idx) => {
                      return (
                        <RichText
                          entries={entries}
                          content={content}
                          key={idx}
                          nodeType={nodeType}
                          richTextStyle={{
                            paragraph: TEXT_BASE_CLASS['body'] + ' text-black',
                          }}
                        />
                      );
                    })}
                    {callToAction && (
                      <CtfCta
                        field={callToAction}
                        entries={entries}
                        onClick={(e, { localUrl, url, name }) =>
                          analytics({
                            window,
                            document,
                            detail: getClickCtaDetail(e, { localUrl, url, name }),
                          })
                        }
                      />
                    )}
                  </div>

                </div>
              </div>
            )}
          </div>
          <div
            className={cls(
              'w-full transform',
              'md:w-1/2 lg:w-1/4 md:translate-x-20'
            )}
          >
            {cards && (
              <div className="aaco-carousel-card relative">
                <div
                  className={isDD ? cls(
                    `aaco-carousel-card__background`,
                    'absolute bottom-0 left-0 right-0',
                    {
                      'top-1/3': isDD,
                      'top-[109px] md:top-[190px]': isWH,
                    },
                    'bg-white shadow-xl',
                    'p-5 dd-en:px-[18px] dd-kr:px-[18px]',
                    {
                      'md:p-10': !carouselContentStyle?.compactSpace,
                    }
                  ) : ""}
                ></div>
                <div
                  className={cls(
                    `aaco-carousel-card__image -mb-5 transform translate-x-5 -translate-y-5  dd-kr:rounded-[8px] dd-en:rounded-[8px] ${
                      !carouselContentStyle?.compactSpace &&
                      'md:-mb-10 translate-x-0 sm:-translate-x-28 md:translate-x-2 lg:translate-x-6 2xl:-translate-x-4 md:-translate-y-10'
                    }`
                  )}
                >
                  <Swiper
                    centeredSlides={true}
                    className="aaco-swiper"
                    controller={{ control: [secondSwiper, thirdSwiper] }}
                    modules={SWIPER_MODULES}
                    onSwiper={setFirstSwiper}
                    spaceBetween={60}
                    speed={SWIPER_SPEED}
                    slidesPerView={'auto'}
                  >
                    {cards.map((card, index) => {
                      const content = getContent(card.sys?.id, entries);
                      const {
                        dynamicAsset,
                        cloudinaryAssets,
                        primaryCTA,
                        secondaryCTA,
                        previewAsset,
                        title,
                      } = content.fields;
                      const asset =
                        cloudinaryAssets ?? dynamicAsset ?? previewAsset;
                      return (
                        <SwiperSlide key={index}>
                          <div
                            className={cls('aaco-carousel-card__slide', {
                              'w-full h-full': isDD,
                              'w-full h-fit': isWH,
                            })}
                          >
                            {asset && (
                              <div
                                className={cls('aaco-card__image', 'w-full ', {
                                  'h-full': isDD,
                                })}
                              >
                                <DynamicAsset
                                  assets={assets}
                                  entries={entries}
                                  field={asset}
                                  container="portrait"
                                  title={title}
                                />
                              </div>
                            )}
                          </div>
                        </SwiperSlide>
                      );
                    })}
                  </Swiper>
                </div>
                <div
                  className={`aaco-carousel-card__content lg:-translate-x-4 xl:translate-x-8 2xl:translate-x-0 relative py-4 dd-en:py-5 dd-kr:py-5 ${
                    carouselContentStyle?.compactSpace && 'md:py-6'
                  } ${isWH ? "max-w-[330px]" : ""}`}
                >
                  <Swiper
                    controller={{ control: [firstSwiper] }}
                    effect="fade"
                    fadeEffect={{ crossFade: true }}
                    modules={[...SWIPER_MODULES, EffectFade]}
                    navigation={SWIPER_NAVIGATION}
                    onSwiper={setSecondSwiper}
                    pagination={SWIPER_PAGINATION}
                    speed={SWIPER_SPEED}
                  >
                    {cards.map((card, index) => {
                      const content = getContent(card.sys?.id, entries);
                      let { primaryCTA, text, subRichText, title } = content?.fields;
                      text =
                        text && text.sys
                          ? getContent(text.sys.id, entries)?.fields.text
                          : text;
                      subRichText =
                        subRichText && subRichText.sys
                          ? getContent(subRichText.sys.id, entries)?.fields.subRichText
                          : subRichText;

                      return (
                        <SwiperSlide key={index}>
                          <div
                            className={`content relative px-5 dd-en:px-[18px] dd-kr:px-[18px] dd-en:md:px-[38px] dd-kr:md:px-[38px] ${
                              featuredCardTitle ? 'hidden' : ''
                            } ${
                              !carouselContentStyle?.compactSpace && 'md:px-4'
                            } ${isWH ? "border-black border-t-1 border-b-1 py-4 !px-0" : ""}`}
                          >
                            {title && (
                              <>
                                {card?.fields?.slug ? (
                                  <CtaWrapper
                                    page={card?.fields?.slug}
                                    classNames={carouselContentStyle?.linkStyle}
                                  >
                                    <Text tag={isDD ? 'h5' : 'h7'} style="aaco-card__title uppercase mt-2">
                                      {title}
                                    </Text>
                                  </CtaWrapper>
                                ) : isDD ? (<h5 className="font-heading text-h5 uppercase mt-2">
                                {title}
                              </h5>) : (
                                <Text tag="body" style="uppercase">
                                  {title}
                                </Text>
                              )}
                              </>
                            )}
                            {text && !text.content && (
                              <p className="text-body text-black uppercase">{text}</p>
                            )}
                            {subRichText &&
                              subRichText.content &&
                              subRichText.content.length > 0 &&
                              subRichText.content.map(
                                ({ nodeType, content }, index) => (
                                  <RichText
                                    entries={entries}
                                    nodeType={nodeType}
                                    content={content}
                                    key={index}
                                    richTextStyle={{
                                      paragraph: {
                                        customFont:
                                          TEXT_BASE_CLASS['body'] +
                                          ' uppercase !font-pro-mono text-[13.5px]'
                                      },
                                    }}
                                  />
                                )
                              )}
                            {primaryCTA && primaryCTA.sys && (
                              <div className="aaco-card__cta mt-2">
                                <CtfCta field={primaryCTA} entries={entries} />
                              </div>
                            )}
                            {primaryCTA &&
                              !primaryCTA.sys &&
                              !carouselContentStyle.compactSpace && (
                                <div
                                  className={cls('aaco-carousel-hero__cta', {
                                    'mt-4': isDD,
                                    'my-2': isWH,
                                  })}
                                >
                                  <Cta
                                    buttonProps={{
                                      size:"small",
                                      invert: isDD,
                                      name:
                                        isKorean
                                          ? '자세히 보기'
                                          : 'Learn more',
                                      type: isWH ? CtfCtaStyle.PRIMARY : CtfCtaStyle.SECONDARY,
                                      style: cls('w-fit', {
                                        'text-black text-button': isWH,
                                      }),
                                    }}
                                    iconProps={{
                                      name: 'arrow-right',
                                      height: 15,
                                      width: 15,
                                      style:
                                        'text-acacia dd-kr:text-actual-dd-gold dd-en:text-actual-dd-gold',
                                      hasIconBorder: true,
                                    }}
                                    linkProps={{
                                      url: primaryCTA,
                                    }}
                                  />
                                </div>
                              )}
                          </div>
                        </SwiperSlide>
                      );
                    })}
                    <div
                      className={`swiper-control flex items-center ${isWH ? "justify-start" : "justify-end"} ${
                        featuredCardTitle
                          ? `py-1 px-5 ${
                              !carouselContentStyle?.compactSpace && 'md:px-10'
                            }`
                          : `absolute bottom-0 left-5 dd-en:right-[18px] dd-kr:right-[18px] dd-kr:md:right-10 dd-en:md:right-10 ${
                              !carouselContentStyle?.compactSpace &&
                              'dd-en:md:right-10 dd-kr:md:right-10 left-4'
                            } z-20`
                      }`}
                    >
                      <div className="aaco-carousel-normal__prev">
                        <Icon
                          name="chevron-left"
                          height={isDD ? 12 : 14}
                          width={isDD ? 6 : 14}
                          className="text-black"
                        />
                      </div>
                      <div
                        className={cls('flex-grow-0 w-auto', {
                          'mx-5 text-grey-6 text-small': isDD,
                          'mx-4 text-dark-warm-grey font-body text-body': isWH,
                        })}
                      >
                        <div className="aaco-carousel-normal__pagination"></div>
                      </div>
                      <div className="aaco-carousel-normal__next">
                        <Icon
                          name="chevron-right"
                          className="text-black"
                          height={isDD ? 12 : 14}
                          width={isDD ? 6 : 14}
                        />
                      </div>
                    </div>
                  </Swiper>
                </div>
              </div>
            )}
          </div>
          <div className="flex-1"></div>
        </div>
        {isWH && <hr className="border-black hidden md:block my-6 relative top-4" />}
      </div>
    </div>
  );
}

CarouselNormal.propTypes = {
  assets: PropTypes.array,
  carouselContentStyle: PropTypes.object,
  entries: PropTypes.array,
  fields: PropTypes.object,
};


export default CarouselNormal;
